import {
  Pagination as MuiPagination,
  PaginationItem,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "@/app/constants/constants";
import PaginationProps from "@/app/interfaces/props/PaginationProps";

const Pagination = ({
  onPageChange,
  onPageSizeChange,
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  pageSizeOptions = PAGINATION_PAGE_SIZE_OPTIONS,
  disabled,
}: PaginationProps) => {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <Box
      display="flex"
      flexDirection={isMobile ? "column" : "row"}
      alignItems="center"
      justifyContent="space-between"
      bgcolor="#e5e5e5"
      border={"1px solid #ccc"}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent={isMobile ? "center" : "flex-start"}
        width={isMobile ? "100%" : "auto"}
      >
        <IconButton
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1 || disabled}
          size={isMobile ? "small" : "medium"}
        >
          <FirstPageIcon fontSize={isMobile ? "small" : "medium"} />
        </IconButton>
        <IconButton
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || disabled}
          size={isMobile ? "small" : "medium"}
        >
          <KeyboardArrowLeftIcon fontSize={isMobile ? "small" : "medium"} />
        </IconButton>
        <MuiPagination
          count={totalPages}
          page={currentPage}
          onChange={(_, page) => onPageChange(page)}
          color="primary"
          siblingCount={isMobile ? 0 : 1}
          boundaryCount={isMobile ? 0 : 1}
          disabled={disabled}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              slots={{
                previous: KeyboardArrowLeftIcon,
                next: KeyboardArrowRightIcon,
              }}
              sx={{
                display:
                  (item.type === "previous" || item.type === "next") && isMobile
                    ? "none"
                    : "",
              }}
            />
          )}
          size={isMobile ? "small" : "medium"}
          hidePrevButton={true}
          hideNextButton={true}
        />
        <IconButton
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || disabled}
          size={isMobile ? "small" : "medium"}
        >
          <KeyboardArrowRightIcon fontSize={isMobile ? "small" : "medium"} />
        </IconButton>
        <IconButton
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || disabled}
          size={isMobile ? "small" : "medium"}
        >
          <LastPageIcon fontSize={isMobile ? "small" : "medium"} />
        </IconButton>
        <Select
          value={pageSize}
          disabled={disabled}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          size="small"
          sx={{ ml: isMobile ? 0 : 2, bgcolor: "white" }}
        >
          {pageSizeOptions.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent={isMobile ? "center" : "flex-end"}
        width={isMobile ? "100%" : "auto"}
      >
        <Typography
          variant="body2"
          ml={isMobile ? 1 : 2}
          mr={isMobile ? 1 : 0}
          whiteSpace="nowrap"
        >
          {totalItems === 0
            ? "0 items"
            : `${startItem} - ${endItem} of ${totalItems} items`}
        </Typography>
        <IconButton
          disabled={disabled}
          sx={{ ml: isMobile ? 0 : 2 }}
          onClick={() => window.location.reload()}
          title="Refresh"
          size={isMobile ? "small" : "medium"}
        >
          <RefreshIcon fontSize={isMobile ? "small" : "medium"} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Pagination;
